import React, { useState, useCallback, useEffect } from 'react';
import {
    CheckCircle,
    WarningCircle,
    FloppyDisk,
    Eye,
    X,
    Hash,
    Bank,
    CurrencyDollar,
    HouseLine,
    BatteryCharging,
    SunDim,
    Ruler,
    DotOutline,
} from '@phosphor-icons/react';
import { Spin, message, Row, Col } from 'antd';
import quoteServices from '../../../../services/oneButtonServices/quoteServices';
import { useOneButtonStore } from '../../../../store/oneButtonStore';
import { ViewTrinityContractModal, type ViewTrinityContractRow } from './ViewTrinityContractModal';
import DashboardModal from './DashboardModal';
import {
    type QuoteField,
    getQuoteFieldsLayout,
    modalWantsSbLayout,
    normPartnerKey,
    resolveAngularSolarFinanceKey,
} from './OBDQuoteDetailsModal.layouts';
import { OBDSelect } from './form-elements';
import './oneButton-css/OneButtonFormControls.css';

/** Angular `quote-vesrion` / `contract-list`: `CombinedRate.Total` and per-line `Rates`. */
interface CombinedRateBlock {
    total: string;
    rows: { name: string; value: string }[];
}

/** Angular `more-details-model.formatDecimalPlacesAndDollar` — USD + fixed decimal places. */
function formatCurrencyUsdDecimals(value: unknown, decimalPlaces: number): string {
    const n = Number(value);
    const num = Number.isFinite(n) ? n : 0;
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: decimalPlaces,
        maximumFractionDigits: decimalPlaces,
    }).format(num);
}

function formatDollarRate3(n: number): string {
    if (!Number.isFinite(n)) return '—';
    return formatCurrencyUsdDecimals(n, 3);
}

/** `$` + en-US locale, 2 decimal places — shared by SB / Battery / Roof mappers. */
function formatUsdLocale2(v: unknown): string {
    if (v == null || v === '') return '$0.00';
    const n = Number(v);
    if (!Number.isFinite(n)) return '$0.00';
    return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function isValidClonedQuote(
    clonedQuote?: Record<string, any> | null | false,
): clonedQuote is Record<string, any> {
    if (!clonedQuote || typeof clonedQuote !== 'object') return false;
    return Object.keys(clonedQuote).length > 0;
}

function withClonedQuoteComparison(
    formatted: string,
    clonedFormatted: string,
    clonedQuote?: Record<string, any> | null | false,
): string {
    if (!isValidClonedQuote(clonedQuote)) return formatted;
    return `${formatted} (vs. ${clonedFormatted} from cloned quote)`;
}

/** `formatUsdLocale2` with optional cloned-quote comparison suffix. */
function formatUsdLocale2Clone(
    value: unknown,
    clonedValue: unknown,
    clonedQuote?: Record<string, any> | null | false,
): string {
    return withClonedQuoteComparison(
        formatUsdLocale2(value),
        formatUsdLocale2(clonedValue),
        clonedQuote,
    );
}

/** `formatUsdLocale2` when present, else `emptyFallback` (default `—`). */
function formatUsdLocale2OrClone(
    value: unknown,
    clonedValue: unknown,
    clonedQuote?: Record<string, any> | null | false,
    emptyFallback = '—',
): string {
    const formatted =
        value != null && String(value) !== '' ? formatUsdLocale2(value) : emptyFallback;
    const clonedFormatted =
        clonedValue != null && String(clonedValue) !== ''
            ? formatUsdLocale2(clonedValue)
            : emptyFallback;
    return withClonedQuoteComparison(formatted, clonedFormatted, clonedQuote);
}

/** `value != null ? formatUsdLocale2(value) : '$0.00'` with optional clone compare. */
function formatUsdLocale2NullOrZeroClone(
    value: unknown,
    clonedValue: unknown,
    clonedQuote?: Record<string, any> | null | false,
): string {
    const formatted = value != null ? formatUsdLocale2(value) : '$0.00';
    const clonedFormatted = clonedValue != null ? formatUsdLocale2(clonedValue) : '$0.00';
    return withClonedQuoteComparison(formatted, clonedFormatted, clonedQuote);
}

function formatDollarRate3OrEmptyClone(
    value: unknown,
    clonedValue: unknown,
    clonedQuote?: Record<string, any> | null | false,
    emptyFallback = '—',
): string {
    const formatted =
        value != null && value !== '' ? formatDollarRate3(Number(value)) : emptyFallback;
    const clonedFormatted =
        clonedValue != null && clonedValue !== ''
            ? formatDollarRate3(Number(clonedValue))
            : emptyFallback;
    return withClonedQuoteComparison(formatted, clonedFormatted, clonedQuote);
}

function readTotalEpcRaw(data: Record<string, any>): unknown {
    return data.TotalEPC ?? data.TrinityEPC ?? data.TotalEpcWatt ?? data.Dealer_Epc ?? data.DealerEPC;
}

function readDealerEpcRaw(data: Record<string, any>): unknown {
    return data.Dealer_Epc ?? data.DealerEPC;
}

function readPpwRaw(data: Record<string, any>): unknown {
    return data.EpcPerWattPvOnly != null && String(data.EpcPerWattPvOnly) !== '' && data.EpcPerWattPvOnly != 0
        ? data.EpcPerWattPvOnly
        : data.Desired_PPW_Net;
}

function readTotalEpcNetRaw(data: Record<string, any>): unknown {
    return data.TrinityLoanEPC ?? data.TrinityEPC ?? readTotalEpcRaw(data);
}

/** Battery-only solar rate column: fixed 3 decimals after `$`. */
function formatUsdRate3Plain(v: unknown): string {
    if (v == null || v === '') return '$0.000';
    const n = Number(v);
    if (!Number.isFinite(n)) return '$0.000';
    return `$${n.toFixed(3)}`;
}

/** One cell in Combined Rate breakdown (`child.Name` / `child.Rate` in Angular template). */
function formatCombinedRateCell(value: unknown): string {
    const n = Number(value);
    if (!Number.isFinite(n)) return '—';
    return formatCurrencyUsdDecimals(n, 3);
}

/**
 * `quote-vesrion.component.ts` / `contract-list.component.ts`:
 * CombinedRate: { Total: ele.Solar_Rate + ele.BatteryRate, Rates: [ { Name:'Solar', Rate: Solar_Rate }, { Name:'Battery', Rate: BatteryRate } ] }
 */
function readSolarRateField(data: Record<string, unknown>): number {
    const v =
        data.Solar_Rate ?? data.solar_Rate ?? data.solarRate ?? data.SolarRate;
    const n = Number(v);
    return Number.isFinite(n) ? n : NaN;
}

function readBatteryRateField(data: Record<string, unknown>): number {
    const v = data.BatteryRate ?? data.Battery_Rate ?? data.battery_Rate ?? data.batteryRate;
    const n = Number(v);
    return Number.isFinite(n) ? n : NaN;
}

/**
 * Same object Angular builds in `quote-vesrion` / `contract-list` for each quote row (`ele`):
 * Total = ele.Solar_Rate + ele.BatteryRate, Rates = Solar / Battery with those rates only.
 */
function combinedRateFromEleFields(data: Record<string, unknown>): CombinedRateBlock | null {
    const sr = readSolarRateField(data) || 0;
    const br = readBatteryRateField(data) || 0;
    if (!Number.isFinite(sr) || !Number.isFinite(br)) return null;
    return {
        total: formatCurrencyUsdDecimals(sr + br, 3),
        rows: [
            { name: 'Solar', value: formatCurrencyUsdDecimals(sr, 3) },
            { name: 'Battery', value: formatCurrencyUsdDecimals(br, 3) },
        ],
    };
}

function emptyCombinedRatePlaceholder(): CombinedRateBlock {
    return {
        total: '—',
        rows: [
            { name: 'Solar', value: '—' },
            { name: 'Battery', value: '—' },
        ],
    };
}

/**
 * Always use Angular’s `ele` formula when both `Solar_Rate` and `BatteryRate` exist.
 * Only if either rate is missing do we fall back to a server-provided `CombinedRate` object.
 */
function buildCombinedRateBlock(data: Record<string, unknown>): CombinedRateBlock {
    const fromEle = combinedRateFromEleFields(data);
    if (fromEle != null) {
        return fromEle;
    }

    const cr = data.CombinedRate as { Total?: unknown; Rates?: unknown[] } | undefined;
    if (cr && Array.isArray(cr.Rates) && cr.Rates.length > 0) {
        const rawRows = cr.Rates as Record<string, unknown>[];
        const rows = rawRows
            .map((r) => {
                const name = String(r.Name ?? r.name ?? '').trim();
                const rateVal = r.Rate ?? r.rate;
                return {
                    name,
                    value: formatCombinedRateCell(rateVal),
                };
            })
            .filter((r) => r.name);

        if (rows.length === 0) {
            return emptyCombinedRatePlaceholder();
        }

        let totalNum = Number(cr.Total);
        if (!Number.isFinite(totalNum)) {
            totalNum = rawRows.reduce((sum, r) => {
                const n = Number(r.Rate ?? r.rate);
                return sum + (Number.isFinite(n) ? n : 0);
            }, 0);
        }

        return {
            total: formatCurrencyUsdDecimals(totalNum, 3),
            rows,
        };
    }

    return emptyCombinedRatePlaceholder();
}

/**
 * GetQuoteDetails may return a flat quote, or RetrieveContract-like shapes:
 * `{ data: { ... } }` / `{ QuoteContract: [ { ProductType, QuoteId, ... } ] }` with no `ProductType` on the root.
 * Merge the matching quote row so ProductType branches run and form values bind.
 */
function extractQuoteDetailsRecord(raw: unknown, quoteIdStr: string): Record<string, unknown> {
    if (raw == null || typeof raw !== 'object') {
        return {};
    }
    let root = raw as Record<string, unknown>;
    const want = String(quoteIdStr).trim();

    for (let depth = 0; depth < 5; depth++) {
        const qc = root.QuoteContract ?? root.quoteContract;
        if (Array.isArray(qc) && qc.length > 0) {
            const byId = qc.find((item) => {
                if (item == null || typeof item !== 'object') return false;
                const q = item as Record<string, unknown>;
                return String(q.QuoteId ?? q.quoteId ?? '') === want;
            }) as Record<string, unknown> | undefined;
            const row = (byId ?? (qc[0] as Record<string, unknown>)) ?? {};
            return { ...root, ...row };
        }

        const inner = root.data ?? root.Data ?? root.result ?? root.Result;
        if (inner != null && typeof inner === 'object' && !Array.isArray(inner)) {
            root = inner as Record<string, unknown>;
            continue;
        }
        break;
    }

    return root;
}

function isSbProductType(data: Record<string, unknown>): boolean {
    const pt = String(data.ProductType ?? data.productType ?? '').trim();
    const u = pt.toUpperCase();
    return (
        u === 'SB' ||
        u === 'SBR' ||
        pt === 'Solar, Battery' ||
        pt === 'Solar, Battery, Roof'
    );
}

/** `SR` / `Solar, Roof` — Angular `EnabledFields["Solar, Roof"]`. */
function isSrProductType(data: Record<string, unknown>): boolean {
    const pt = String(data.ProductType ?? data.productType ?? '').trim();
    const compact = pt.replace(/\s/g, '');
    return pt === 'SR' || pt === 'Solar, Roof' || compact === 'Solar,Roof';
}

/** Matches Angular agreements / Lightreach casing (`Lightreach` vs `LightReach`). */
function isLightreachPartner(fp: unknown): boolean {
    return normPartnerKey(String(fp || '')) === 'lightreach';
}

/** Angular `MoreDetails.EnabledFields.Solar.Sunlight` — `generate-quote.constant.ts`. */
function isSunlightPartner(fp: unknown): boolean {
    return normPartnerKey(String(fp || '')) === 'sunlight';
}

/**
 * Angular roof/solar “Terms and APR” line: `Term` years @ `Apr` or `TermAPR` %.
 * `more-details-model` / contract-list style display.
 */
function formatSunlightTermsAndAprLine(data: Record<string, unknown>): string {
    const termRaw = data.Term ?? data.term;
    const termNum = Number(termRaw);
    const term = Number.isFinite(termNum) ? termNum : 0;
    const aprRaw = data.Apr ?? data.TermAPR ?? data.APR ?? data.apr;
    const aprNum = Number(aprRaw);
    const apr = Number.isFinite(aprNum) ? aprNum : 0;
    return `${term} Years @ ${apr.toFixed(2)} %`;
}

/**
 * RetrieveContract / quote rows often use `ProductType: "S"`; Angular MoreDetails still treats as Solar.
 */
function isSolarProductType(data: Record<string, unknown>): boolean {
    const pt = String(data.ProductType ?? data.productType ?? '').trim();
    return pt === 'Solar' || pt === 'S';
}

/** Angular `more-details-model` table: `PPW (Solar Only)` → `EpcPerWattPvOnly` (fallback `Desired_PPW_Net`). */
function formatAnnualProductionKwh(raw: unknown): string {
    if (raw == null || String(raw).trim() === '') return '';
    const n = Number(raw);
    if (!Number.isFinite(n)) return `${String(raw).trim()} kWh`;
    if (n >= 100) return `${Math.round(n).toLocaleString('en-US')} kWh`;
    return `${n.toLocaleString('en-US')} kWh`;
}

function productTypeDisplayLabel(pt: string): string {
    const u = pt.trim();
    if (u === 'S' || u === 'Solar') return 'Solar';
    return u;
}

/**
 * SB “Pricing Method” line: `FinanceMethod - PricingMethod` (e.g. Lease Storage - Solar Rate).
 * Some APIs put the full phrase in `PricingMethod` while `FinanceMethod` is only the first segment,
 * which produced "Lease Storage - Lease Storage - Solar Rate".
 */
function pricingMethodDisplayLine(financeMethod: string, pricingMethod: string): string {
    const fm = financeMethod.trim();
    const pm = pricingMethod.trim();
    if (!fm && !pm) return '—';
    if (!fm) return pm;
    if (!pm) return fm;
    if (pm === fm) return pm;
    if (pm.startsWith(fm)) {
        const rest = pm.slice(fm.length);
        if (rest === '' || /^\s*[-–—]/.test(rest)) return pm;
    }
    return `${fm} - ${pm}`;
}

/** Build `ViewTrinityContractRow` from `GetQuoteDetails` — Angular `viewModal(..., 'Solar'|'Battery'|'Roof')`. */
function buildTrinityProposalRowFromQuoteApi(
    data: Record<string, unknown>,
    quoteId: string,
    productLabel: string,
    target: 'battery' | 'roof' | 'solar',
): ViewTrinityContractRow {
    const rawPt = String(data.ProductType ?? '').trim();
    let productTypeCode = rawPt;
    if (target === 'battery') {
        if (rawPt === 'Battery Only') productTypeCode = 'BO';
    } else if (target === 'roof') {
        if (rawPt === 'Roof Only') productTypeCode = 'RO';
        if (rawPt === 'Roof') productTypeCode = 'R';
        if (rawPt === 'RO') productTypeCode = 'RO';
    } else {
        if (rawPt === 'Solar') productTypeCode = 'S';
    }
    const fp = String(data.FinancePartner ?? '').trim();
    const fm = String(data.FinanceMethod ?? data.PricingMethod ?? '').trim();
    const defaultProductLabel =
        target === 'roof' ? 'Roof Only' : target === 'battery' ? 'Battery Only' : 'Solar';
    return {
        key: `quote-details-${quoteId}`,
        quoteIdRaw: String(data.QuoteId ?? quoteId),
        productTypeCode,
        productType: productLabel.trim() || defaultProductLabel,
        financePartner: fp,
        financePartnerRaw: fp,
        financeMethod: fm,
        manualOverride: Boolean(data.ManualOverride),
        loadCalculatorDataExists: Boolean(data.LoadCalculatorDataExists),
    };
}

interface QuoteValues {
    [key: string]:
        | string
        | boolean
        | { label: string; status: 'good' | 'warning' | 'bad' }
        | CombinedRateBlock;
}

function generateFallbackQuote(partner: string, product: string, monthly: number, apr: number): QuoteValues {
    const isLoan = partner !== 'Cash';
    const systemSizeKw = product.includes('Battery') ? 10.2 : product.includes('Roof') ? 9.8 : 8.4;
    const annualProd = Math.round(systemSizeKw * 1450);
    const ppw = (monthly * 12 * 25 / (systemSizeKw * 1000)).toFixed(2);
    const epc = (systemSizeKw * 1000 * 2.98).toFixed(2);

    const solarBase: QuoteValues = {
        quoteRating: setMessage(),
        quoteNumber: `#${10000 + Math.floor(Math.random() * 90000)}`,
        financePartner: partner,
        pricingMethod: partner === 'Cash' ? 'Cash Purchase' : 'PPW - Solar Rate',
        pricingValue: partner === 'Cash' ? `$${monthly.toLocaleString()}` : `$0.${Math.round(apr * 100 + 12)}`,
        solarRate: `$0.${Math.round(145 + Math.random() * 30)}`,
        solarEscalator: isLoan ? `${(2.5 + Math.random() * 1).toFixed(2)}` : '0.00',
        grossPpw: `$${ppw}`,
        totalEpcWatt: `$${Number(epc).toLocaleString()}`,
        annualProduction: `${annualProd.toLocaleString()} kWh`,
        monthlyPayment: partner === 'Cash' ? '' : `$${monthly.toFixed(2)}`,
        year1Savings: `$${(annualProd * 0.16).toFixed(2)}`,
        downPayment: '$0.00',
        solarProposal: true,
        systemSize: `${(systemSizeKw * 1000).toLocaleString()} kW`,
    };
    if (partner === 'Cash' && product === 'Solar') {
        return {
            ...solarBase,
            AmountOnPhysicalInstallStart: '$0.00',
            AmountOnPhysicalInstallCompletion: '$0.00',
            AmountOnContractCompletion: '$0.00',
            AmountOnWorkCompletion: '$0.00',
        };
    }
    return solarBase;
}
function setMessage(
    desc?: string,
    quoteRating?: string,
    productType?: string,
    isCOQuote?: boolean
): { label: string; status: 'good' | 'warning' | 'bad' } {

    let status: 'good' | 'warning' | 'bad' = 'good';
    if (quoteRating === 'Yellow') status = 'warning';
    else if (quoteRating === 'Red') status = 'bad';
    const isSolar =
        productType?.includes('Solar') || String(productType ?? '').trim() === 'S';
    let message = desc || '';
    if (isCOQuote) {
        return {
            label: 'COQ',
            status
        };
    }
    if (quoteRating === 'Green' && !isSolar) {
        message = message || 'Acceptable pricing';
        return { label: message, status };
    }
    if (quoteRating === 'Yellow') {
        if (message && isSolar) {
            if (message.includes('commission') && message.includes('below minimum')) {
                message = 'Pricing is below minimum.';
            } else {
                const firstSentence = message.split('.')[0];
                message =
                    firstSentence === 'Pricing below minimum'
                        ? 'Pricing is below minimum.'
                        : `${firstSentence}.`;
            }
        }
        return { label: message, status };
    }
    if (quoteRating === 'Red') {
        return { label: message, status };
    }
    return { label: '', status };
}

function formatSolarSystemSizeFromValue(systemSize: unknown, unit: 'KW' | 'kW' = 'KW'): string {
    if (systemSize == null || String(systemSize) === '') return '';
    return `${parseFloat(String(systemSize)).toFixed(3)} ${unit}`;
}

function formatSolarSystemSizeKw(
    data: Record<string, unknown>,
    clonedQuote?: Record<string, any>,
    unit: 'KW' | 'kW' = 'KW',
): string {
    const formatted = formatSolarSystemSizeFromValue(data.SystemSize, unit);
    if (!isValidClonedQuote(clonedQuote)) return formatted;
    const clonedFormatted = formatSolarSystemSizeFromValue(clonedQuote.SystemSize, unit);
    if (!formatted && !clonedFormatted) return formatted;
    return withClonedQuoteComparison(formatted, clonedFormatted, clonedQuote);
}

/**
 * Angular `more-details-model` Cash rows: Deposit/Financed + payment milestones (`tableFilds` dollar cells).
 * Used for Solar Cash, Solar+Battery Cash, Solar+Roof Cash (Solar branch overrides milestones).
 */
function cashDepositAndMilestoneValues(
    data: Record<string, any>,
    clonedQuote?: Record<string, any>,
): QuoteValues {
    const fmt = (v: unknown, clonedV: unknown) =>
        formatUsdLocale2NullOrZeroClone(v, clonedV, null);
    return {
        Deposit_Amount: formatUsdLocale2Clone(data.Deposit_Amount, null, null),
        Financed_Amount: formatUsdLocale2NullOrZeroClone(
            data.Financed_Amount,
            null,
            null,
        ),
        AmountOnPhysicalInstallStart: fmt(data.AmountOnPhysicalInstallStart, null),
        AmountOnPhysicalInstallCompletion: fmt(
            data.AmountOnPhysicalInstallCompletion,
           null,
        ),
        AmountOnContractCompletion: fmt(data.AmountOnContractCompletion, null),
        AmountOnWorkCompletion: fmt(data.AmountOnWorkCompletion, null),
    };
}

/**
 * Shared Solar row values for Good Leap, Cash, Service Finance, IGS, Sunnova, and default `QUOTE_FIELDS`.
 * Field keys align with `tableFilds` / `EnabledFields` in `generate-quote.constant.ts`.
 */
function buildSolarSharedFormValues(
    data: Record<string, any>,
    quoteId: string,
    partner: string,
    clonedQuote?: Record<string, any>,
): QuoteValues {
    const fp = String(data.FinancePartner ?? partner ?? '');
    const fm = String(data.FinanceMethod ?? '').trim();
    const pm = String(data.PricingMethod ?? '').trim();
    const pricingMethodLine = pricingMethodDisplayLine(fm, pm);

    const totalEpcRaw = readTotalEpcRaw(data);
    const dealerEpcRaw = readDealerEpcRaw(data);
    const trinityEpcRaw = data.TrinityEPC;
    const trinityLoanEpcRaw = data.TrinityLoanEPC;
    const ppwRaw = readPpwRaw(data);
    const cq = isValidClonedQuote(clonedQuote) ? clonedQuote : undefined;
    const clonedTotalEpcRaw = cq ? readTotalEpcRaw(cq) : undefined;
    const clonedDealerEpcRaw = cq ? readDealerEpcRaw(cq) : undefined;
    const clonedTrinityEpcRaw = cq?.TrinityEPC;
    const clonedPpwRaw = cq ? readPpwRaw(cq) : undefined;
    const clonedTotalEpcNetRaw = cq ? readTotalEpcNetRaw(cq) : undefined;
    const clonedY1Raw = cq ? (cq.Year1Saving ?? cq.Year1Savings) : undefined;

    let termAprEscalator = '';
    if (data.Escalator != null && String(data.Escalator).trim() !== '') {
        termAprEscalator = `${parseFloat(String(data.Escalator)).toFixed(2)}`;
    } else {
        const t = String(data.TermAPR ?? '').trim();
        termAprEscalator = t && t !== 'N/A' ? t : '';
    }

    const pmLower = pm.toLowerCase();

    const totalEpcNetRaw = readTotalEpcNetRaw(data);
    const monthlyFmt = formatUsdLocale2NullOrZeroClone(
        data.MonthlyPayment,
        (data.MonthlyPayment)?cq?.MonthlyPayment:null,
        (data.MonthlyPayment)?clonedQuote:null,
    );
    const y1Raw = data.Year1Saving ?? data.Year1Savings;
    const base: QuoteValues = {
        quoteRating: setMessage(data.QuoteRatingDesc, data.QuoteRating, data.ProductType, data.IsCOQuote),
        quoteNumber: String(data.QuoteId ?? quoteId),
        productDisplay: productTypeDisplayLabel(String(data.ProductType ?? '')),
        financePartner: fp,
        /** Legacy `QUOTE_FIELDS` (default Solar) uses single `PricingMethod` column. */
        pricingMethod: pm || (partner === 'Cash' ? 'Cash Purchase' : 'PPW - Solar Rate'),
        pricingMethodLine,
        termAprEscalator: termAprEscalator || '—',
        pricingValue: getPricingValueFormatted(data, pmLower, clonedQuote),
        solarRate: formatUsdRate3Plain(data.Solar_Rate),
        solarEscalator:
            data.Escalator != null && String(data.Escalator) !== ''
                ? `${parseFloat(String(data.Escalator)).toFixed(2)}`
                : '',
        grossPpw: formatUsdLocale2OrClone(data.Desired_PPW_Net, cq?.Desired_PPW_Net, clonedQuote),
        ppwSolarOnly: formatUsdLocale2OrClone(ppwRaw, clonedPpwRaw, clonedQuote),
        totalEpcWatt: formatUsdLocale2OrClone(totalEpcRaw, clonedTotalEpcRaw, clonedQuote),
        totalEpcNet: formatUsdLocale2OrClone(totalEpcNetRaw, clonedTotalEpcNetRaw, clonedQuote),
        trinityEpcNet: formatUsdLocale2OrClone(dealerEpcRaw, clonedDealerEpcRaw, clonedQuote),
        totalEpcGross: formatUsdLocale2OrClone(trinityEpcRaw, clonedTrinityEpcRaw, clonedQuote),
        annualProduction: formatAnnualProductionKwh(data.AnnualProduction ?? data.EstimatedProduction),
        monthlyPayment: monthlyFmt,
        monthlySunnovaPayment: monthlyFmt,
        advancePaymentAmount: monthlyFmt,
        year1Savings: formatUsdLocale2OrClone(y1Raw, clonedY1Raw, clonedQuote, '$0.00'),
        downPayment: formatUsdLocale2Clone(data.DownPayment, cq?.DownPayment, clonedQuote),
        systemSize: formatSolarSystemSizeKw(data, clonedQuote),
        quoteExpiresIn: data.QuoteExpiresIn != null ? String(data.QuoteExpiresIn) : '',
        solarProposal: true,
        solarProposalEye: true,
        sunnovaSystemId: String(data.SystemId ?? data.SystemProjectId ?? ''),
        monthlyFinancePaymentNoItc: formatUsdLocale2Clone(
            data.Monthly_Finance_Payment_NoITCPayback,
            cq?.Monthly_Finance_Payment_NoITCPayback,
            clonedQuote,
        ),
        itcAmount: formatUsdLocale2Clone(data.ITC_Amount, cq?.ITC_Amount, clonedQuote),
        energyCommunity: String(data.Energy_Community ?? ''),
        domesticContent: String(data.DomesticContent ?? 'Yes'),
        termsAndApr: formatSunlightTermsAndAprLine(data as Record<string, unknown>),
        SunnovaQuote: data.SunnovaQuote??null
    };

    if (normPartnerKey(fp) === 'cash') {
        return {
            ...base,
            ...cashDepositAndMilestoneValues(data, clonedQuote),
        };
    }

    return base;
}

/** Maps GetQuoteDetails payload to grid `formValues` by ProductType (Angular parity). */
function buildQuoteFormValuesFromApi(
    data: Record<string, any>,
    quoteId: string,
    partner: string,
    product: string,
    fallbackQuote: QuoteValues,
    clonedQuote?: Record<string, any>
): QuoteValues {
    if (isSolarProductType(data)) {
        const fp = String(data.FinancePartner ?? partner ?? '');
        const fm = String(data.FinanceMethod ?? '').trim();
        const pm = String(data.PricingMethod ?? '').trim();
        const pricingMethodLine = pricingMethodDisplayLine(fm, pm);
        const totalEpcRaw = readTotalEpcRaw(data);
        const ppwRaw = readPpwRaw(data);
        const cq = isValidClonedQuote(clonedQuote) ? clonedQuote : undefined;
        const clonedTotalEpcRaw = cq ? readTotalEpcRaw(cq) : undefined;
        const clonedPpwRaw = cq ? readPpwRaw(cq) : undefined;

        let termAprEscalator = '';
        if (data.Escalator != null && String(data.Escalator).trim() !== '') {
            termAprEscalator = `${parseFloat(String(data.Escalator)).toFixed(2)}`;
        } else {
            const t = String(data.TermAPR ?? '').trim();
            termAprEscalator = t && t !== 'N/A' ? t : '';
        }

        const pmLower = pm.toLowerCase();

        /** Angular `MoreDetails.EnabledFields.Solar.Lightreach` + `more-details-model` Lightreach field swaps. */
        if (isLightreachPartner(fp)) {
            return {
                quoteRating: setMessage(data.QuoteRatingDesc, data.QuoteRating, data.ProductType, data.IsCOQuote),
                quoteNumber: String(data.QuoteId ?? quoteId),
                productDisplay: productTypeDisplayLabel(String(data.ProductType ?? '')),
                financePartner: fp,
                pricingMethodLine,
                termAprEscalator: termAprEscalator || '—',
                pricingValue: getPricingValueFormatted(data, pmLower, clonedQuote),
                ppwSolarOnly: formatUsdLocale2OrClone(ppwRaw, clonedPpwRaw, clonedQuote),
                totalEpcWatt: formatUsdLocale2OrClone(totalEpcRaw, clonedTotalEpcRaw, clonedQuote),
                annualProduction: formatAnnualProductionKwh(data.AnnualProduction ?? data.EstimatedProduction),
                monthlyPayment: formatUsdLocale2NullOrZeroClone(
                    data.MonthlyPayment,
                    cq?.MonthlyPayment,
                    clonedQuote,
                ),
                productwiseMonthlyPaymentDTOs:data.productwiseMonthlyPaymentDTOs?? [],
                solarProposalEye: true,
                quoteExpiresIn: data.QuoteExpiresIn != null ? String(data.QuoteExpiresIn) : '',
                systemSize: formatSolarSystemSizeKw(data, clonedQuote),
            };
        }

        /** Angular `MoreDetails.EnabledFields.Solar.Sunlight` — `Loan - EPC` style pricing line, Trinity EPC, Terms and APR. */
        if (isSunlightPartner(fp)) {
            const annualRaw = data.EstimatedProduction ?? data.AnnualProduction;
            const nAnnual = Number(annualRaw);
            /** Some contract rows send kWh as `14.514` instead of `14514` — scale when clearly sub-year-kWh. */
            const annualScaled =
                Number.isFinite(nAnnual) &&
                nAnnual > 0 &&
                nAnnual < 1000 &&
                String(annualRaw).includes('.') &&
                nAnnual * 1000 >= 1000
                    ? nAnnual * 1000
                    : annualRaw;
            const annualOut = formatAnnualProductionKwh(annualScaled);

            return {
                quoteRating: setMessage(data.QuoteRatingDesc, data.QuoteRating, data.ProductType, data.IsCOQuote),
                quoteNumber: String(data.QuoteId ?? quoteId),
                financePartner: fp,
                pricingMethodLine,
                pricingValue: getPricingValueFormatted(data, pmLower, clonedQuote),
                solarRate: formatUsdRate3Plain(data.Solar_Rate),
                grossPpw: formatUsdLocale2OrClone(data.Desired_PPW_Net, cq?.Desired_PPW_Net, clonedQuote),
                totalEpcWatt: formatUsdLocale2OrClone(totalEpcRaw, clonedTotalEpcRaw, clonedQuote),
                annualProduction: annualOut,
                monthlyPayment: formatUsdLocale2NullOrZeroClone(
                    data.MonthlyPayment,
                    cq?.MonthlyPayment,
                    clonedQuote,
                ),
                termsAndApr: formatSunlightTermsAndAprLine(data as Record<string, unknown>),
                solarProposalEye: true,
                quoteExpiresIn: data.QuoteExpiresIn != null ? String(data.QuoteExpiresIn) : '',
                systemSize: formatSolarSystemSizeKw(data, clonedQuote),
            };
        }

        /** Good Leap, Cash, Service Finance, IGS, Sunnova, and fallback `QUOTE_FIELDS` — Angular `EnabledFields.Solar`. */
        return buildSolarSharedFormValues(data, quoteId, partner, clonedQuote);
    }

    if (data.ProductType === 'RR') {
        const cq = isValidClonedQuote(clonedQuote) ? clonedQuote : undefined;
        const totalNet = readTotalEpcRaw(data);
        const clonedTotalNet = cq ? readTotalEpcRaw(cq) : undefined;
        return {
            quoteNumber: String(data.QuoteId ?? quoteId),
            financePartner: String(data.FinancePartner ?? partner ?? ''),
            pricingValue: formatUsdLocale2NullOrZeroClone(data.PricingValue, cq?.PricingValue, clonedQuote),
            totalEpcNet: formatUsdLocale2OrClone(totalNet, clonedTotalNet, clonedQuote, '$0.00'),
            rrSystemSize:
                data.RRSystemSize != null && String(data.RRSystemSize).trim() !== ''
                    ? String(data.RRSystemSize)
                    : '',
            grossPpw: formatUsdLocale2OrClone(data.Desired_PPW_Net, cq?.Desired_PPW_Net, clonedQuote),
            downPayment: formatUsdLocale2Clone(data.DownPayment, cq?.DownPayment, clonedQuote),
            monthlyPayment: formatUsdLocale2NullOrZeroClone(
                data.MonthlyPayment,
                cq?.MonthlyPayment,
                clonedQuote,
            ),
            quoteExpiresIn: data.QuoteExpiresIn != null ? String(data.QuoteExpiresIn) : '',
        };
    }

    if (isSrProductType(data)) {
        const fp = String(data.FinancePartner ?? partner ?? '');
        const fm = String(data.FinanceMethod ?? data.Quote_FinanceMethod ?? '').trim();
        const pm = String(data.PricingMethod ?? data.Quote_PricingMethod ?? '').trim();
        const pricingMethodLine = pricingMethodDisplayLine(fm, pm);
        const totalEpcRaw = readTotalEpcRaw(data);
        const dealerEpcRaw = readDealerEpcRaw(data);
        const trinityEpcRaw = data.TrinityEPC;
        const ppwRaw = readPpwRaw(data);
        const cq = isValidClonedQuote(clonedQuote) ? clonedQuote : undefined;
        const clonedTotalEpcRaw = cq ? readTotalEpcRaw(cq) : undefined;
        const clonedDealerEpcRaw = cq ? readDealerEpcRaw(cq) : undefined;
        const clonedTrinityEpcRaw = cq?.TrinityEPC;
        const clonedPpwRaw = cq ? readPpwRaw(cq) : undefined;
        const clonedTotalEpcNetRaw = cq ? readTotalEpcNetRaw(cq) : undefined;
        const clonedY1Raw = cq ? (cq.Year1Savings ?? cq.Year1Saving) : undefined;
        let termAprEscalator = '';
        if (data.Escalator != null && String(data.Escalator).trim() !== '') {
            termAprEscalator = `${parseFloat(String(data.Escalator)).toFixed(2)}`;
        } else {
            const t = String(data.TermAPR ?? '').trim();
            termAprEscalator = t && t !== 'N/A' ? t : '';
        }
        const pmLower = String(data.PricingMethod ?? '').toLowerCase();
        const pricingValueFormatted = getPricingValueFormatted(data, pmLower, clonedQuote);
        const y1Raw = data.Year1Savings ?? data.Year1Saving;
        const rrSubtotal = formatUsdLocale2OrClone(data.RR_Price, cq?.RR_Price, clonedQuote);
        const totalEpcNetRaw = readTotalEpcNetRaw(data);
        const monthlyFmtSr = formatUsdLocale2NullOrZeroClone(
            data.MonthlyPayment,
            cq?.MonthlyPayment,
            clonedQuote,
        );
        const srBase = {
            quoteRating: setMessage(data.QuoteRatingDesc, data.QuoteRating, data.ProductType, data.IsCOQuote),
            quoteNumber: String(data.QuoteId ?? quoteId),
            productDisplay: 'Solar, Roof',
            financePartner: fp,
            pricingMethodLine,
            pricingValue: pricingValueFormatted,
            solarRate: formatUsdRate3Plain(data.Solar_Rate),
            termAprEscalator: termAprEscalator || '—',
            systemSize: formatSolarSystemSizeKw(data, clonedQuote),
            ppwSolarOnly: formatUsdLocale2OrClone(ppwRaw, clonedPpwRaw, clonedQuote),
            rrSubtotal,
            trinityEpcNet: formatUsdLocale2OrClone(dealerEpcRaw, clonedDealerEpcRaw, clonedQuote),
            totalEpcGross: formatUsdLocale2OrClone(trinityEpcRaw, clonedTrinityEpcRaw, clonedQuote),
            totalEpcNet: formatUsdLocale2OrClone(totalEpcNetRaw, clonedTotalEpcNetRaw, clonedQuote),
            monthlySunnovaPayment: monthlyFmtSr,
            advancePaymentAmount: monthlyFmtSr,
            annualProduction: formatAnnualProductionKwh(data.AnnualProduction ?? data.EstimatedProduction),
            year1Savings: formatUsdLocale2OrClone(y1Raw, clonedY1Raw, clonedQuote, '$0.00'),
            roofProposalEye: true,
            solarProposalEye: true,
            sunnovaSystemId: String(data.SystemId ?? data.SystemProjectId ?? ''),
            quoteExpiresIn: data.QuoteExpiresIn != null ? String(data.QuoteExpiresIn) : '',
            monthlyPayment: monthlyFmtSr,
            downPayment: formatUsdLocale2Clone(data.DownPayment, cq?.DownPayment, clonedQuote),
            grossPpw: formatUsdLocale2OrClone(data.Desired_PPW_Net, cq?.Desired_PPW_Net, clonedQuote),
            productwiseMonthlyPaymentDTOs: data.productwiseMonthlyPaymentDTOs ?? [],
        };
        return normPartnerKey(fp) === 'cash'
            ? { ...srBase, ...cashDepositAndMilestoneValues(data, clonedQuote) }
            : srBase;
    }

    if (isSbProductType(data) || modalWantsSbLayout(product, data)) {
        const pt = String(data.ProductType ?? '');
        const productDisplay =
            pt === 'SBR' || pt === 'Solar, Battery, Roof'
                ? 'Solar, Battery, Roof'
                : 'Solar, Battery';
        /** Angular `quote-vesrion` maps `FinanceMethod: ele.Quote_FinanceMethod`, `PricingMethod: ele.PricingMethod`; raw rows may only expose `Quote_*`. */
        const fm = String(
            data.FinanceMethod ?? data.Quote_FinanceMethod ?? '',
        ).trim();
        const pm = String(
            data.PricingMethod ?? data.Quote_PricingMethod ?? '',
        ).trim();
        const pricingMethodLine = pricingMethodDisplayLine(fm, pm);
        const pricingValueSb = formatDollarRate3OrEmptyClone(
            data.PricingValue,
            clonedQuote?.PricingValue,
            clonedQuote,
        );
        const y1Raw = data.Year1Savings ?? data.Year1Saving;
        const totalEpcRaw = readTotalEpcRaw(data);
        const dealerEpcRaw = readDealerEpcRaw(data);
        const trinityEpcRaw = data.TrinityEPC;
        const ppwRaw = readPpwRaw(data);
        const cq = isValidClonedQuote(clonedQuote) ? clonedQuote : undefined;
        const clonedTotalEpcRaw = cq ? readTotalEpcRaw(cq) : undefined;
        const clonedDealerEpcRaw = cq ? readDealerEpcRaw(cq) : undefined;
        const clonedTrinityEpcRaw = cq?.TrinityEPC;
        const clonedPpwRaw = cq ? readPpwRaw(cq) : undefined;
        const clonedTotalEpcNetSb = cq ? readTotalEpcNetRaw(cq) : undefined;
        const clonedY1Raw = cq ? (cq.Year1Savings ?? cq.Year1Saving) : undefined;
        let termAprEscalatorSb = '';
        if (data.Escalator != null && String(data.Escalator).trim() !== '') {
            termAprEscalatorSb = `${parseFloat(String(data.Escalator)).toFixed(2)}`;
        } else {
            const t = String(data.TermAPR ?? '').trim();
            termAprEscalatorSb = t && t !== 'N/A' ? t : '';
        }
        const totalEpcNetSb = readTotalEpcNetRaw(data);
        const monthlyFmtSb = formatUsdLocale2NullOrZeroClone(
            data.MonthlyPayment,
            cq?.MonthlyPayment,
            clonedQuote,
        );
        const fpSb = String(data.FinancePartner ?? partner ?? '');

        const sbBase = {
            quoteRating: setMessage(data.QuoteRatingDesc, data.QuoteRating, data.ProductType, data.IsCOQuote),
            quoteNumber: String(data.QuoteId ?? quoteId),
            productDisplay,
            financePartner: fpSb,
            pricingMethodLine,
            pricingValue: pricingValueSb,
            combinedRate: buildCombinedRateBlock(data as Record<string, unknown>),
            solarEscalator:
                data.Escalator != null && String(data.Escalator) !== ''
                    ? `${parseFloat(String(data.Escalator)).toFixed(2)}`
                    : '',
            termAprEscalator: termAprEscalatorSb || '—',
            grossPpw: formatUsdLocale2OrClone(data.Desired_PPW_Net, cq?.Desired_PPW_Net, clonedQuote),
            ppwSolarOnly: formatUsdLocale2OrClone(ppwRaw, clonedPpwRaw, clonedQuote),
            trinityEpcNet: formatUsdLocale2OrClone(dealerEpcRaw, clonedDealerEpcRaw, clonedQuote),
            totalEpcGross: formatUsdLocale2OrClone(trinityEpcRaw, clonedTrinityEpcRaw, clonedQuote),
            totalEpcNet: formatUsdLocale2OrClone(totalEpcNetSb, clonedTotalEpcNetSb, clonedQuote),
            totalEpcLabel: formatUsdLocale2OrClone(totalEpcRaw, clonedTotalEpcRaw, clonedQuote),
            totalEpcWatt: formatUsdLocale2OrClone(totalEpcRaw, clonedTotalEpcRaw, clonedQuote),
            annualProduction: formatAnnualProductionKwh(data.AnnualProduction ?? data.EstimatedProduction),
            monthlyPayment: monthlyFmtSb,
            monthlySunnovaPayment: monthlyFmtSb,
            advancePaymentAmount: monthlyFmtSb,
            downPayment: formatUsdLocale2Clone(data.DownPayment, cq?.DownPayment, clonedQuote),
            year1Savings: formatUsdLocale2OrClone(y1Raw, clonedY1Raw, clonedQuote, '$0.00'),
            Battery_Price_Sub_Total: formatUsdLocale2Clone(
                data.Battery_Price_Sub_Total,
                cq?.Battery_Price_Sub_Total,
                clonedQuote,
            ),
            Battery_Adder_Sub_Total: formatUsdLocale2Clone(
                data.Battery_Adder_Sub_Total,
                cq?.Battery_Adder_Sub_Total,
                clonedQuote,
            ),
            systemSize: formatSolarSystemSizeKw(data, clonedQuote, 'kW'),
            /** Angular mapped row uses `BatteryManufacturer` / `BatteryQuantity`; API may send `BatteryManufacturerName` / `Quantity`. */
            batteryManufacturer:
                (data.BatteryManufacturerName != null &&
                String(data.BatteryManufacturerName).trim()
                    ? String(data.BatteryManufacturerName)
                    : data.BatteryManufacturer != null &&
                        String(data.BatteryManufacturer).trim()
                      ? String(data.BatteryManufacturer)
                      : '') || '',
            batteryModel:
                (data.BatteryModelName != null && String(data.BatteryModelName).trim()
                    ? String(data.BatteryModelName)
                    : String(data.BatteryModel ?? '')) || '',
            batteryCount: String(data.Quantity ?? data.BatteryQuantity ?? 0),
            solarProposalEye: true,
            quoteExpiresIn: data.QuoteExpiresIn != null ? String(data.QuoteExpiresIn) : '',
            sunnovaSystemId: String(data.SystemId ?? data.SystemProjectId ?? ''),
            monthlyFinancePaymentNoItc: formatUsdLocale2Clone(
                data.Monthly_Finance_Payment_NoITCPayback,
                cq?.Monthly_Finance_Payment_NoITCPayback,
                clonedQuote,
            ),
            itcAmount: formatUsdLocale2Clone(data.ITC_Amount, cq?.ITC_Amount, clonedQuote),
            productwiseMonthlyPaymentDTOs: data.productwiseMonthlyPaymentDTOs ?? [],
        };
        return normPartnerKey(fpSb) === 'cash'
            ? { ...sbBase, ...cashDepositAndMilestoneValues(data, clonedQuote) }
            : sbBase;
    }

    if (
        data.ProductType === 'Battery Only' ||
        data.ProductType === 'BO' ||
        data.ProductType === 'B'
    ) {
        const cq = isValidClonedQuote(clonedQuote) ? clonedQuote : undefined;
        const totalNet = readTotalEpcRaw(data);
        const clonedTotalNet = cq ? readTotalEpcRaw(cq) : undefined;
        const pricingMethodDisplay =
            (typeof data.PricingMethod === 'string' && data.PricingMethod.trim()) ||
            (typeof data.FinanceMethod === 'string' && data.FinanceMethod.trim()) ||
            (partner === 'Cash' ? 'Cash Purchase' : 'PPW - Solar Rate');
        const termAprStr =
            data.Term != null && data.TermAPR != null && String(data.TermAPR) !== 'N/A'
                ? `${data.Term} Years @ ${data.TermAPR} %`
                : data.TermAPR != null && String(data.TermAPR).trim()
                  ? String(data.TermAPR)
                  : 'N/A';

        let termAprEscalatorBo = '';
        if (data.Escalator != null && String(data.Escalator).trim() !== '') {
            termAprEscalatorBo = `${parseFloat(String(data.Escalator)).toFixed(2)}`;
        } else {
            const t = String(data.TermAPR ?? '').trim();
            termAprEscalatorBo = t && t !== 'N/A' ? t : '';
        }
        const dealerEpcBo = readDealerEpcRaw(data);
        const clonedDealerEpcBo = cq ? readDealerEpcRaw(cq) : undefined;
        const trinityGrossBo = data.TrinityEPC;
        const clonedTrinityGrossBo = cq?.TrinityEPC;
        const monthlyFmtBo = formatUsdLocale2NullOrZeroClone(
            data.MonthlyPayment,
            cq?.MonthlyPayment,
            clonedQuote,
        );
        const totalEpcFmtBo = formatUsdLocale2OrClone(totalNet, clonedTotalNet, clonedQuote, '$0.00');

        const commonFields: QuoteValues = {
            quoteNumber: String(data.QuoteId ?? quoteId),
            productDisplay: 'Battery Only',
            financePartner: data.FinancePartner || partner,
            pricingMethod: pricingMethodDisplay,
            pricingValue: formatUsdLocale2NullOrZeroClone(data.PricingValue, cq?.PricingValue, clonedQuote),
            solarRate: formatUsdRate3Plain(data.Solar_Rate),
            totalEpcWatt: totalEpcFmtBo,
            totalEpcLabel: totalEpcFmtBo,
            MonthlyPayment: monthlyFmtBo,
            downPayment: formatUsdLocale2Clone(data.DownPayment, cq?.DownPayment, clonedQuote),
            PricingTier: (data.Pricing_Type != null && String(data.Pricing_Type).trim()
                ? String(data.Pricing_Type)
                : data.PricingTier) || '',
            Battery_Price_Sub_Total: formatUsdLocale2Clone(
                data.Battery_Price_Sub_Total,
                cq?.Battery_Price_Sub_Total,
                clonedQuote,
            ),
            Battery_Adder_Sub_Total: formatUsdLocale2Clone(
                data.Battery_Adder_Sub_Total,
                cq?.Battery_Adder_Sub_Total,
                clonedQuote,
            ),
            quoteExpiresIn: data.QuoteExpiresIn != null ? String(data.QuoteExpiresIn) : '',
            Deposit_Amount: formatUsdLocale2Clone(data.Deposit_Amount, cq?.Deposit_Amount, clonedQuote),
            Financed_Amount: formatUsdLocale2NullOrZeroClone(
                data.Financed_Amount,
                cq?.Financed_Amount,
                clonedQuote,
            ),
            batteryManufacturer:
                (data.BatteryManufacturerName != null && String(data.BatteryManufacturerName).trim()
                    ? String(data.BatteryManufacturerName)
                    : data.BatteryManufacturer) || '',
            batteryModel:
                (data.BatteryModelName != null && String(data.BatteryModelName).trim()
                    ? String(data.BatteryModelName)
                    : data.BatteryModel) || '',
            batteryCount: String(
                data.Quantity ?? data.BatteryQuantity ?? 0,
            ),
            AmountOnPhysicalInstallCompletion: formatUsdLocale2NullOrZeroClone(
                data.AmountOnPhysicalInstallCompletion,
                cq?.AmountOnPhysicalInstallCompletion,
                clonedQuote,
            ),
            AmountOnWorkCompletion: formatUsdLocale2NullOrZeroClone(
                data.AmountOnWorkCompletion,
                cq?.AmountOnWorkCompletion,
                clonedQuote,
            ),
            termAprEscalator: termAprEscalatorBo || '—',
            trinityEpcNet: formatUsdLocale2OrClone(dealerEpcBo, clonedDealerEpcBo, clonedQuote),
            totalEpcGross: formatUsdLocale2OrClone(trinityGrossBo, clonedTrinityGrossBo, clonedQuote),
            monthlySunnovaPayment: monthlyFmtBo,
            batteryProposalEye: true,
            sunnovaSystemId: String(data.SystemId ?? data.SystemProjectId ?? ''),
        };

        const isCash = data.FinancePartner === 'Cash';
        if (!isCash) {
            return { ...commonFields, TermAPR: termAprStr };
        }
        return { ...commonFields };
    }

    if (
        data.ProductType === 'Roof Only' ||
        data.ProductType === 'Roof' ||
        data.ProductType === 'RO'
    ) {
        const cq = isValidClonedQuote(clonedQuote) ? clonedQuote : undefined;
        const roofProdRaw = data.RoofProduct != null && String(data.RoofProduct).trim()
            ? String(data.RoofProduct)
            : String(data.Roofing_product ?? '');
        const isCash = data.FinancePartner === 'Cash';
        const totalEpcRoof =
            data.TotalEPC != null
                ? data.TotalEPC
                : data.TrinityEPC != null
                  ? data.TrinityEPC
                  : data.Dealer_Epc ?? data.DealerEPC;
        const clonedTotalEpcRoof = cq
            ? cq.TotalEPC != null
                ? cq.TotalEPC
                : cq.TrinityEPC != null
                  ? cq.TrinityEPC
                  : cq.Dealer_Epc ?? cq.DealerEPC
            : undefined;
        const dealerEpcRoof = readDealerEpcRaw(data);
        const clonedDealerEpcRoof = cq ? readDealerEpcRaw(cq) : undefined;
        const trinityGrossRoof = data.TrinityEPC;
        const clonedTrinityGrossRoof = cq?.TrinityEPC;
        const rrSubtotal = formatUsdLocale2OrClone(data.RR_Price, cq?.RR_Price, clonedQuote);
        const fmRoof = String(data.FinanceMethod ?? data.Quote_FinanceMethod ?? '').trim();
        const pmRoof = String(data.PricingMethod ?? data.Quote_PricingMethod ?? '').trim();
        const pricingMethodLineRoof = pricingMethodDisplayLine(fmRoof, pmRoof);
        let termAprEscalatorRoof = '';
        if (data.Escalator != null && String(data.Escalator).trim() !== '') {
            termAprEscalatorRoof = `${parseFloat(String(data.Escalator)).toFixed(2)}`;
        } else {
            const t = String(data.TermAPR ?? '').trim();
            termAprEscalatorRoof = t && t !== 'N/A' ? t : '';
        }
        const termsAprLine = `${data.Term ?? ''} Years @ ${data.Apr ?? data.TermAPR ?? '0'} %`;
        const productDisplayRoof =
            data.ProductType === 'RO' || data.ProductType === 'Roof Only' ? 'Roof Only' : 'Roof';
        const monthlyFmtRoof = formatUsdLocale2NullOrZeroClone(
            data.MonthlyPayment,
            cq?.MonthlyPayment,
            clonedQuote,
        );

        const commonFields = {
            quoteNumber: `${data.QuoteId || quoteId}`,
            financePartner: data.FinancePartner || '',
            pricingValue: formatUsdLocale2NullOrZeroClone(data.PricingValue, cq?.PricingValue, clonedQuote),
            roofManufacturer:
                (data.ManufacturerName != null && String(data.ManufacturerName).trim()
                    ? String(data.ManufacturerName)
                    : '') ||
                (['Dynasty', 'Cambridge'].includes(roofProdRaw) ? 'IKO' : ''),
            roofProduct: roofProdRaw,
            slopePrice: formatUsdLocale2NullOrZeroClone(data.Price_Per_Sqft, cq?.Price_Per_Sqft, clonedQuote),
            monthlyPayment: monthlyFmtRoof,
            downPayment: formatUsdLocale2NullOrZeroClone(data.DownPayment, cq?.DownPayment, clonedQuote),
            depositAmount: formatUsdLocale2Clone(data.Deposit_Amount, cq?.Deposit_Amount, clonedQuote),
            financedAmount: formatUsdLocale2NullOrZeroClone(
                data.Financed_Amount,
                cq?.Financed_Amount,
                clonedQuote,
            ),
            rrSubtotal,
            productDisplay: productDisplayRoof,
            pricingMethodLine: pricingMethodLineRoof,
            termAprEscalator: termAprEscalatorRoof || '—',
            trinityEpcNet: formatUsdLocale2OrClone(dealerEpcRoof, clonedDealerEpcRoof, clonedQuote),
            totalEpcGross: formatUsdLocale2OrClone(trinityGrossRoof, clonedTrinityGrossRoof, clonedQuote),
            totalEpcNet: formatUsdLocale2OrClone(totalEpcRoof, clonedTotalEpcRoof, clonedQuote, '$0.00'),
            monthlySunnovaPayment: monthlyFmtRoof,
            roofProposalEye: true,
            sunnovaSystemId: String(data.SystemId ?? data.SystemProjectId ?? ''),
            termsAndApr: termsAprLine,
            termAndApr: termsAprLine,
            dealerFeePct:
                data.Dealer_Fee_Percentage != null && String(data.Dealer_Fee_Percentage).trim() !== ''
                    ? String(data.Dealer_Fee_Percentage)
                    : '',
        };

        if (isCash) {
            const pricingMethodRoof =
                (typeof data.PricingMethod === 'string' && data.PricingMethod.trim()) ||
                (typeof data.FinanceMethod === 'string' && data.FinanceMethod.trim()) ||
                '';
            return {
                ...commonFields,
                pricingMethod: pricingMethodRoof,
                solarRate:
                    data.Solar_Rate != null && data.Solar_Rate !== ''
                        ? `$${Number(data.Solar_Rate).toFixed(3)}`
                        : '$0.000',
                totalEpc: formatUsdLocale2OrClone(totalEpcRoof, clonedTotalEpcRoof, clonedQuote, '$0.00'),
                quoteExpiresIn: data.QuoteExpiresIn != null ? String(data.QuoteExpiresIn) : '',
                plusAddersUponCompletion: formatUsdLocale2NullOrZeroClone(
                    data.AmountOnWorkCompletion,
                    cq?.AmountOnWorkCompletion,
                    clonedQuote,
                ),
            };
        }
        return {
            ...commonFields,
            financeMethod: String(data.Quote_FinanceMethod ?? data.FinanceMethod ?? '').trim(),
            shingleColor: data.Shingle_Color || '',
            normalRoofSquares: data.Normal_Roof_Squares != null ? String(data.Normal_Roof_Squares) : '0',
            lowRoofSquares: data.Low_Roof_Squares != null ? String(data.Low_Roof_Squares) : '0',
        };
    }

    return fallbackQuote;
}

const getPricingValueFormatted = (
    data: Record<string, any>,
    pmLower: string,
    clonedQuote?: Record<string, any>,
) => {
    const formatPricing = (v: unknown) =>
        v != null && v !== ''
            ? pmLower.includes('rate')
                ? formatDollarRate3(Number(v))
                : formatUsdLocale2(v)
            : '$0.00';
    const pricingValueFormatted = formatPricing(data.PricingValue);
    if (!isValidClonedQuote(clonedQuote)) return pricingValueFormatted;
    return withClonedQuoteComparison(
        pricingValueFormatted,
        formatPricing(clonedQuote.PricingValue),
        (!!Number(data.PricingValue))?clonedQuote:null,
    );
};

interface OBDQuoteDetailsModalProps {
    partner: string;
    product: string;
    monthly: number;
    apr: number;
    mode: 'view' | 'edit';
    quoteId?: string;
    onClose: () => void;
    /** Required to open Battery Proposal (`SunnovaTrinityContractModelComponent` / `TrinityProposalModal`). */
    opportunityId?: string | number | null;
    salesforceId?: string | null;
    opportunityDetails?: unknown;
    onAfterAction?: () => Promise<void>;
}

const OBDQuoteDetailsModal: React.FC<OBDQuoteDetailsModalProps> = ({
    partner,
    product,
    monthly,
    apr,
    mode,
    quoteId,
    onClose,
    opportunityId,
    salesforceId,
    opportunityDetails,
    onAfterAction,
}) => {
    const fallbackQuote = React.useMemo(
        () => generateFallbackQuote(partner, product, monthly, apr),
        [partner, product, monthly, apr],
    );

    const [formValues, setFormValues] = useState<QuoteValues | null>(null);
    /** Raw API object for Trinity proposal modal (Angular `viewModal` row). */
    const [quoteDetailPayload, setQuoteDetailPayload] = useState<Record<string, unknown> | null>(null);
    const [clonedQuoteDetails, setClonedQuoteDetails] = useState<Record<string, unknown> | null>(null);
    const [proposalModalOpen, setProposalModalOpen] = useState(false);
    const [proposalRow, setProposalRow] = useState<ViewTrinityContractRow | null>(null);

    const [loading, setLoading] = useState(true);
    const theme = useOneButtonStore((s) => s.theme);

    useEffect(() => {
        if (quoteId) {
            setLoading(true);
            setQuoteDetailPayload(null);
            setClonedQuoteDetails(null);

            quoteServices.getQuoteDetails(quoteId)
                .then((res) => {
                    const raw:any = (res as { data?: unknown }).data ?? res;
                    /** Dynamic quote JSON from GetQuoteDetails / nested QuoteContract — index access must stay loose for TS. */
                    const data = extractQuoteDetailsRecord(raw, String(quoteId)) as Record<string, any>;
                    setQuoteDetailPayload(data as Record<string, unknown>);
                    
                    if(raw.Cloned_Quote_Id){
                        quoteServices.getQuoteDetails(raw.Cloned_Quote_Id).then((res1) =>{
                            const raw1:any = (res1 as { data?: unknown }).data ?? res1;
                            const data1 = extractQuoteDetailsRecord(raw1, String(raw.Cloned_Quote_Id)) as Record<string, any>;
                            setClonedQuoteDetails(extractQuoteDetailsRecord(data1, String(raw.Cloned_Quote_Id)) as Record<string, unknown>);
                            setFormValues(buildQuoteFormValuesFromApi(data, String(quoteId), partner, product, fallbackQuote, data1));
                        })
                    } else {
                        setFormValues(buildQuoteFormValuesFromApi(data, String(quoteId), partner, product, fallbackQuote));
                    }
                })
                .catch(() => {
                    setQuoteDetailPayload(null);
                    setFormValues(fallbackQuote);
                })
                .finally(() => setLoading(false));
        } else {
            setQuoteDetailPayload(null);
            setFormValues(mode === 'edit' ? { ...fallbackQuote, quoteNumber: '(New)' } : fallbackQuote);
            setLoading(false);
        }
    }, [quoteId, fallbackQuote, mode, partner, product]);

    const isEdit = mode === 'edit';

    const handleChange = useCallback((key: string, value: string | boolean) => {
        setFormValues((prev) => (prev ? { ...prev, [key]: value } : prev));
    }, []);

    const handleSave = useCallback(() => {
        onClose();
    }, [onClose]);

    const handleOpenTrinityProposal = useCallback(
        (target: 'battery' | 'roof' | 'solar') => {
            if (!quoteId?.trim()) {
                message.warning('No quote id.');
                return;
            }
            if (!quoteDetailPayload) {
                message.warning('Quote data not loaded.');
                return;
            }
            const oid = opportunityId != null && String(opportunityId).trim() !== '' ? opportunityId : null;
            if (oid == null) {
                message.warning('Missing opportunity id. Load the opportunity in the editor, then try again.');
                return;
            }
            setProposalRow(buildTrinityProposalRowFromQuoteApi(quoteDetailPayload, quoteId, product, target));
            setProposalModalOpen(true);
        },
        [quoteId, quoteDetailPayload, opportunityId, product],
    );

    const getFieldIcon = useCallback((field: QuoteField): React.ReactNode => {
        const key = field.key.toLowerCase();
        const label = field.label.toLowerCase();
        if (field.type === 'proposalEye') return <Eye size={11} weight="fill" />;
        if (field.type === 'rating') return <WarningCircle size={11} weight="fill" />;
        if (key.includes('quote')) return <Hash size={11} weight="bold" />;
        if (key.includes('finance') || key.includes('apr') || label.includes('finance')) {
            return <Bank size={11} weight="fill" />;
        }
        if (
            key.includes('price') ||
            key.includes('payment') ||
            key.includes('savings') ||
            key.includes('epc') ||
            key.includes('ppw') ||
            key.includes('deposit')
        ) return <CurrencyDollar size={11} weight="fill" />;
        if (key.includes('roof')) return <HouseLine size={11} weight="fill" />;
        if (key.includes('battery')) return <BatteryCharging size={11} weight="fill" />;
        if (key.includes('solar') || key.includes('production')) return <SunDim size={11} weight="fill" />;
        if (key.includes('system') || key.includes('size')) return <Ruler size={11} weight="fill" />;
        return <DotOutline size={11} weight="fill" />;
    }, []);

    const renderFieldLabel = useCallback((field: QuoteField) => (
        <span className="obd-quote-v2-label-row">
            <span className="obd-quote-v2-label-icon" aria-hidden="true">{getFieldIcon(field)}</span>
            <span className="obd-quote-v2-label">{field.label}</span>
        </span>
    ), [getFieldIcon]);

    const renderField = (field: QuoteField) => {
        if (!formValues) return null;

        if (field.type === 'proposalEye') {
            const target = field.proposalTarget ?? 'battery';
            const proposalTitle =
                target === 'roof'
                    ? 'View Roof Proposal'
                    : target === 'solar'
                      ? 'View Solar Proposal'
                      : 'View Battery Proposal';
            return (
                <div key={field.key} className="obd-quote-v2-card">
                    {renderFieldLabel(field)}
                    <span className="obd-quote-v2-value">
                        <button
                            type="button"
                            className="obd-quote-v2-proposal-eye"
                            onClick={() => void handleOpenTrinityProposal(target)}
                            disabled={loading || !quoteId}
                            title={proposalTitle}
                            aria-label={proposalTitle}
                        >
                            <Eye size={20} weight="bold" />
                        </button>
                    </span>
                </div>
            );
        }

        if (field.type === 'combinedRate') {
            const block = formValues[field.key] as CombinedRateBlock | undefined;
            if (!block || typeof block !== 'object' || !('total' in block)) return null;
            const rows = block.rows.length > 0 ? block.rows : [
                { name: 'Solar', value: '—' },
                { name: 'Battery', value: '—' },
            ];
            return (
                <div key={field.key} className="obd-quote-v2-card obd-quote-v2-card--combined-rate">
                    {renderFieldLabel(field)}
                    <div className="obd-quote-v2-combined-rate">
                        <div className="obd-quote-v2-combined-rate__total">{block.total}</div>
                        <div className="obd-quote-v2-combined-rate__table-wrap">
                            <table className="obd-quote-v2-combined-rate__table" role="grid">
                                <thead>
                                    <tr>
                                        {rows.map((r) => (
                                            <th key={r.name} scope="col">
                                                {r.name}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        {rows.map((r) => (
                                            <td key={r.name}>{r.value}</td>
                                        ))}
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            );
        }

        const val = formValues[field.key];

        if(val == undefined) return null;
        
        if (field.type === 'rating') {
            const rating = val as { label: string; status: string };
            if (!rating.label) return null;
            return (
                <div key={field.key} className="obd-quote-v2-card">
                    {renderFieldLabel(field)}
                    <span className={`obd-quote-v2-pill obd-quote-v2-pill--${rating.status}`}>
                        {rating.status === 'good' ? <CheckCircle size={14} weight="fill" /> : <WarningCircle size={14} weight="fill" />}
                        {rating.label}
                    </span>
                </div>
            );
        }

        if (field.type === 'toggle') {
            return (
                <div key={field.key} className="obd-quote-v2-card">
                    {renderFieldLabel(field)}
                    <span className="obd-quote-v2-value">
                        {isEdit && field.editable ? (
                            <label className="obd-quote-v2-toggle">
                                <input type="checkbox" checked={!!val} onChange={(e) => handleChange(field.key, e.target.checked)} />
                                <span className="obd-quote-v2-toggle__track" />
                            </label>
                        ) : val ? <CheckCircle size={20} weight="fill" color="#10b981" /> : null}
                    </span>
                </div>
            );
        }

        if (field.type === 'select' && isEdit && field.editable) {
            return (
                <div key={field.key} className="obd-quote-v2-card">
                    {renderFieldLabel(field)}
                    <OBDSelect
                        className="obd-utility__select"
                        value={val as string}
                        onChange={(value) => handleChange(field.key, value)}
                        options={(field.options ?? []).map((opt) => ({ value: opt, label: opt }))}
                    />
                </div>
            );
        }

        if (isEdit && field.editable) {
            return (
                <div key={field.key} className="obd-quote-v2-card">
                    {renderFieldLabel(field)}
                    <input type="text" className="obd-utility__input" value={val as string} onChange={(e) => handleChange(field.key, e.target.value)} />
                </div>
            );
        }

        if(field.label == "Monthly Payment"){
            const productwiseMonthlyPaymentDTOs :any = formValues.productwiseMonthlyPaymentDTOs;
            if(productwiseMonthlyPaymentDTOs?.length > 1){
                return (
                    <>
                        <div key={field.key} className="obd-quote-v2-card">
                            {renderFieldLabel(field)}
                            <span className="obd-quote-v2-value">{val as string} </span>
                            <Row>
                                {
                                    productwiseMonthlyPaymentDTOs.map((ele:any) => {
                                        if(ele.monthlyPayment){
                                            return (
                                                <Col span={12}>
                                                    <div key={field.key} className="obd-quote-v2-card obd-quote-v2-card-half">
                                                        {ele.productType}
                                                        <span className="obd-quote-v2-value">{ele.monthlyPayment as string} </span>
                                                    </div>
                                                </Col>
                                            )
                                        }
                                    })
                                }
                            </Row>
                            
                        </div>
                        
                    </>
                    
                );
            }
        }

        return (
            <div key={field.key} className="obd-quote-v2-card">
                {renderFieldLabel(field)}
                <span className="obd-quote-v2-value">{val as string} </span>
            </div>
        );
    };

    /** Above financing grid (9600), commerce hub (10050), and agreements (10060). */
    const quoteDetailsOverlayZ = 10080;

    const financePartnerResolved = formValues?.financePartner ?? partner;
    const quoteFieldsLayout = getQuoteFieldsLayout({
        productUi: product,
        financePartner: financePartnerResolved,
        data: (quoteDetailPayload ?? {}) as Record<string, unknown>,
    });

    const modalContent = (
        <DashboardModal
            open={true}
            onClose={onClose}
            title={(
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span>{isEdit ? 'New Quote' : 'Quote Details'}</span>
                    <span className="obd-quote-v2-badge">{product}</span>
                </div>
            )}
            footer={isEdit ? (
                <>
                    <button className="obd-modal__cancel" onClick={onClose} type="button">
                        <X size={14} weight="bold" />
                        Cancel
                    </button>
                    <button className="obd-modal__save" onClick={handleSave} disabled={loading} type="button">
                        <FloppyDisk size={13} weight="bold" />
                        Save Quote
                    </button>
                </>
            ) : (
                <button className="obd-modal__cancel" onClick={onClose} type="button">
                    <X size={14} weight="bold" />
                    Close
                </button>
            )}
            width={1120}
            theme={theme}
            zIndex={quoteDetailsOverlayZ}
            rootClassName={`obd-form-controls-modal-root obd-theme-${theme}`}
            className="obd-form-controls-modal"
            maskClosable={false}
        >
            <div className="obd-quote-v2-modal-content" style={{ position: 'relative' }}>
                {loading && (
                    <div style={{
                        position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                        background: theme === 'dark' ? 'rgba(15, 23, 42, 0.4)' : 'rgba(255, 255, 255, 0.4)',
                        backdropFilter: 'blur(1px)', zIndex: 10, borderRadius: 16
                    }}>
                        <Spin size="small" />
                        <span style={{ fontWeight: 500, fontSize: 12, color: theme === 'dark' ? 'rgba(248, 250, 252, 0.98)' : '#1e293b' }}>Loading details…</span>
                    </div>
                )}
                <div className="obd-quote-v2-modal-body">
                    <div className="obd-quote-v2-grid">
                        {quoteFieldsLayout.map(renderField)}
                    </div>
                </div>
            </div>
        </DashboardModal>
    );

    const proposalLayer = (
        <ViewTrinityContractModal
            open={proposalModalOpen}
            mode="proposal"
            row={proposalRow}
            onClose={() => {
                setProposalModalOpen(false);
                setProposalRow(null);
            }}
            theme={theme}
            salesforceId={salesforceId ?? null}
            opportunityId={opportunityId ?? null}
            opportunityDetails={opportunityDetails ?? null}
            onAfterAction={onAfterAction}
        />
    );

    return (
        <>
            {modalContent}
            {proposalLayer}
        </>
    );
};

export default OBDQuoteDetailsModal;
